import { localize, readAppLanguage } from '../i18n';
import useBackHandler from '../hooks/useBackHandler';
import SingleLineText from './ui/InternationalText';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { getDeviceUuid, getStreak } from '../api';

const COLORS = ['#ffe566', '#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#ec4899', '#f59e0b', '#06b6d4'];

const PIECES = Array.from({ length: 36 }).map((_, i) => {
    const angle = (i / 36) * Math.PI * 2 + (i % 2 ? 0.08 : -0.08);
    const distance = 220 + (i * 53) % 140;
    return {
        color: COLORS[i % COLORS.length],
        tx: Math.cos(angle) * distance,
        ty: Math.sin(angle) * distance,
        rot: ((i * 137) % 720) - 360,
        delay: (i * 23) % 220,
        size: 6 + (i % 3) * 2,
    };
});

const AUTO_CLOSE_MS = 1500;

export default function AllDoneCelebration({ open, streak, onClose }) {
    useBackHandler(open, onClose, 2000);
    // 부모 re-render로 onClose 참조가 바뀔 때마다 useEffect가 재실행되어 setTimeout이 reset되는 것을 방지.
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    // 폭죽이 떴을 때 prop streak는 옵티미스틱 직후라 stale. 자체 fetch로 새 streak 받아 표시.
    const [actualStreak, setActualStreak] = useState(streak);
    useEffect(() => {
        if (!open) return undefined;
        setActualStreak(streak);
        let cancelled = false;
        (async () => {
            try {
                const uuid = getDeviceUuid();
                const r = await getStreak(uuid);
                if (!cancelled && typeof r?.streak === 'number') {
                    setActualStreak(r.streak);
                }
            } catch {}
        })();
        return () => { cancelled = true; };
    }, [open, streak]);

    useEffect(() => {
        if (!open) return undefined;
        const t = setTimeout(() => onCloseRef.current?.(), AUTO_CLOSE_MS);
        return () => clearTimeout(t);
    }, [open]);

    if (!open) return null;

    return (
        <Box
            onClick={onClose}
            role="button"
            tabIndex={0}
            sx={{
                position: 'fixed', inset: 0, zIndex: 2000,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                background: 'radial-gradient(circle at 50% 45%, rgba(241,251,243,0.98) 0%, rgba(230,246,234,0.98) 60%, rgba(168,219,177,0.98) 100%)',
                cursor: 'pointer',
                '@keyframes celebrationBigBurst': {
                    '0%': { transform: 'translate(0,0) rotate(0deg) scale(0.4)', opacity: 0 },
                    '12%': { transform: 'translate(0,0) rotate(0deg) scale(1)', opacity: 1 },
                    '100%': { transform: 'translate(var(--tx), var(--ty)) rotate(var(--rot)) scale(0.6)', opacity: 0 },
                },
                '@keyframes celebrationIconPop': {
                    '0%': { transform: 'scale(0) rotate(-30deg)', opacity: 0 },
                    '55%': { transform: 'scale(1.25) rotate(8deg)', opacity: 1 },
                    '100%': { transform: 'scale(1) rotate(0deg)', opacity: 1 },
                },
                '@keyframes celebrationTitlePop': {
                    '0%': { transform: 'translateY(20px) scale(0.85)', opacity: 0 },
                    '100%': { transform: 'translateY(0) scale(1)', opacity: 1 },
                },
            }}
        >
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', ...(readAppLanguage() !== 'ko' && { width: '100%', boxSizing: 'border-box', px: 2 }) }}>
                {PIECES.map((p, i) => (
                    <Box key={i} sx={{
                        position: 'absolute',
                        top: '50%', left: '50%',
                        width: p.size, height: p.size * 1.6,
                        bgcolor: p.color,
                        borderRadius: '2px',
                        pointerEvents: 'none',
                        '--tx': `${p.tx}px`,
                        '--ty': `${p.ty}px`,
                        '--rot': `${p.rot}deg`,
                        opacity: 0,
                        animation: `celebrationBigBurst 1.2s cubic-bezier(0.2,0.7,0.3,1) ${p.delay}ms forwards`,
                    }} />
                ))}
                <CelebrationIcon sx={{
                    fontSize: 96, color: '#1F8A3D',
                    animation: 'celebrationIconPop 0.5s cubic-bezier(0.34,1.56,0.64,1)',
                    filter: 'drop-shadow(0 4px 12px rgba(31,138,61,0.25))',
                }} />
                <SingleLineText sx={{
                    mt: 2,
                    fontSize: '1.9rem', fontWeight: 900, color: '#1F8A3D',
                    letterSpacing: '-0.02em',
                    ...(readAppLanguage() !== 'ko' && { textAlign: 'center' }),
                    animation: 'celebrationTitlePop 0.4s 0.1s cubic-bezier(0.34,1.56,0.64,1) both',
                }}>{localize("오늘 약 모두 완료!")}</SingleLineText>
                {actualStreak > 1 && (
                    <Typography sx={{
                        mt: 1,
                        fontSize: '1.15rem', fontWeight: 700, color: '#2FA351',
                        animation: 'celebrationTitlePop 0.4s 0.2s cubic-bezier(0.34,1.56,0.64,1) both',
                    }}>
                        {actualStreak}{localize("일 연속 복용 중")}</Typography>
                )}
            </Box>
        </Box>
    );
}
